/**
 * Kurze Altersangabe für Listenzeilen — „3m", „2h", „5d", „12w".
 *
 * Sie ersetzt in der Konversationsliste die Datums-Trenner („Heute", „Die
 * letzten 30 Tage", …): pro Zeile steht rechts, wie alt sie ist, statt dass
 * Zwischenüberschriften die Liste in Blöcke schneiden. Unter einer Minute
 * heißt es „jetzt" — eine Zahl wäre dort nur Rauschen.
 *
 * Die Einheit wird als Datum zurückgegeben, nicht als fertiger Text: die
 * Beschriftung kommt aus i18n (`chatBotPage.relativeShort.*`).
 */

/** Einheit der Angabe; `now` trägt keine Zahl. */
export type RelativeShortUnit = "now" | "minute" | "hour" | "day" | "week";

export interface RelativeShort {
  unit: RelativeShortUnit;
  /** Ganzzahlig abgerundet; bei `now` immer 0. */
  count: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/** Zeitpunkte in Millisekunden; `undefined`, wenn der Wert kein Datum ist. */
function toTime(date: unknown): number | undefined {
  if (date == null) return undefined;
  const time =
    date instanceof Date
      ? date.getTime()
      : typeof date === "number"
        ? date
        : new Date(String(date)).getTime();
  return Number.isNaN(time) ? undefined : time;
}

/**
 * Alter eines Zeitpunkts in der gröbsten Einheit, die noch eine Zahl ≥ 1
 * ergibt. Ein Zeitpunkt in der Zukunft (Uhren laufen auseinander) gilt als
 * „jetzt". Ohne verwertbares Datum: `undefined`.
 */
export function relativeTimeShort(
  date: unknown,
  now: Date | number = Date.now(),
): RelativeShort | undefined {
  const time = toTime(date);
  if (time === undefined) return undefined;
  const reference = toTime(now);
  if (reference === undefined) return undefined;

  const elapsed = reference - time;
  if (elapsed < MINUTE) return { unit: "now", count: 0 };
  if (elapsed < HOUR) return { unit: "minute", count: Math.floor(elapsed / MINUTE) };
  if (elapsed < DAY) return { unit: "hour", count: Math.floor(elapsed / HOUR) };
  if (elapsed < WEEK) return { unit: "day", count: Math.floor(elapsed / DAY) };
  return { unit: "week", count: Math.floor(elapsed / WEEK) };
}

/** i18n-Schlüssel je Einheit — `chatBotPage.relativeShort.*`. */
const KEY_BY_UNIT: Record<RelativeShortUnit, string> = {
  now: "chatBotPage.relativeShort.now",
  minute: "chatBotPage.relativeShort.minutes",
  hour: "chatBotPage.relativeShort.hours",
  day: "chatBotPage.relativeShort.days",
  week: "chatBotPage.relativeShort.weeks",
};

/** Übersetzerfunktion in der Form, die `react-i18next` liefert. */
export type Translate = (key: string, options?: { count: number }) => string;

/**
 * Fertige Beschriftung („3m", „jetzt"). Ohne verwertbares Datum: `undefined` —
 * die Zeile lässt die Angabe dann weg, statt einen Platzhalter zu zeigen.
 */
export function formatRelativeShort(
  date: unknown,
  translate: Translate,
  now: Date | number = Date.now(),
): string | undefined {
  const relative = relativeTimeShort(date, now);
  if (!relative) return undefined;
  const key = KEY_BY_UNIT[relative.unit];
  return relative.unit === "now"
    ? translate(key)
    : translate(key, { count: relative.count });
}
